<template>
  <div
    class="flowbuilder relative cursor-grab"
    :class="[templatePreview ? '' : 'h-screen', props.preview ? 'flowbuilder--preview' : '']"
    ref="flowBuilder"
    :style="styleVariables"
    @mousedown="handleMouseDown"
    @mousemove="handleMouseMove"
    @mouseup="handleMouseUp"
    @mouseleave="handleMouseUp"
    @dblclick="disableDoubleClickZoom ? undefined : handleDoubleClick">
    <!-- Slot for controls (zoomer, node adder, etc) -->
    <slot
      name="controls"
      :scale="transform.scale"
      :zoom="zoom"
      :center="center"
      :centerOnNode="centerOnNode"
      :canZoomIn="canZoomIn"
      :canZoomOut="canZoomOut" />

    <!-- Slot for minimap -->
    <slot
      name="minimap"
      :transform="transform"
      :flow-builder-dimensions="flowBuilderDimensions"
      :flow-builder-container-dimensions="flowBuilderContainerDimensions"
      :content-dimensions="contentDimensions" />

    <div
      class="flowbuilder-container select-none"
      ref="flowBuilderContainer"
      :class="{ 'heat-map-active': showHeatMap }">
      <svg class="flowbuilder-canvas">
        <g>
          <FlowConnection
            v-for="connection in connections"
            :connection="connection"
            :key="connection.id" />
        </g>
      </svg>
      <!-- Heat map overlay inside container so it moves with pan/zoom -->
      <slot
        name="heat-map-overlay"
        :connections="connections"
        :node-elements="heatMapNodePositions" />
      <div
        class="flowbuilder-nodes flex flex-col items-center"
        ref="nodesContainer">
        <RootNode :node="funnel" />
      </div>
    </div>

    <!-- Slot for overlay (heat map, etc) -->
    <slot
      name="overlay"
      :nodes-container="nodesContainer"
      :transform="transform"
      :container-dimensions="flowBuilderContainerDimensions" />

    <!-- Slot for preview dialog -->
    <slot name="preview-dialog" :show-preview="showPreview" />

    <!-- Clone mode banner — uses custom CSS class for dark mode since Teleported content
         is outside .flowbuilder scope and Tailwind dark: variants don't apply -->
    <Teleport to="#app">
      <div
        v-if="cloneMode?.active"
        class="clone-mode-banner"
      >
        <component :is="DocumentDuplicateIcon" class="clone-mode-banner-icon" />
        <span class="clone-mode-banner-text">
          Click a dropzone to place the cloned node
        </span>
        <button
          type="button"
          class="clone-mode-banner-button"
          @click="cancelCloneMode"
        >
          Cancel Clone
          <kbd class="clone-mode-banner-kbd">Esc</kbd>
        </button>
      </div>
    </Teleport>

    <!-- Vimium-style hint badges for keyboard navigation (Find Mode) -->
    <HintBadge
      v-for="hint in vimiumHints.hints.value"
      :key="hint.id"
      :label="hint.label"
      :target-rect="hint.rect"
      :input-buffer="vimiumHints.inputBuffer.value"
      :visible="vimiumHints.isActive.value && vimiumHints.isHintHighlighted(hint)"
      :hint-type="hint.type"
    />
    <div ref="teleportRoot" />
  </div>
</template>

<script setup lang="ts">
/**
 * FlowBuilder Component
 *
 * The main flow editor component that provides:
 * - Pan and zoom canvas for flow visualization
 * - Node rendering and connection drawing
 * - Gesture support (pinch, wheel, drag)
 * - Keyboard shortcuts (Cmd+/- for zoom)
 * - Slot-based UI customization
 *
 * Architecture:
 * - Props-driven: All data comes from parent
 * - Provide/Inject: Shares data with child components
 * - Event-driven: Emits events for mutations
 * - Adapter pattern: App-specific functionality
 */
import {
  ref,
  reactive,
  computed,
  provide,
  onMounted,
  nextTick,
  watchEffect,
  watch
} from 'vue'
import {
  useResizeObserver,
  useMutationObserver,
  useMouseInElement,
  useDebounceFn,
  useEventListener,
  onKeyStroke
} from '@vueuse/core'
import { useMotionProperties, useMotionControls } from '@vueuse/motion'
import { useWheel, usePinch } from '@vueuse/gesture'
import { Lethargy } from 'lethargy'

import type { Flow, FlowBuilderProps } from '../types'
import { uuid, funnelDefinitionEach, scoresInFlow } from '../services'
import { useFlow } from '../composables/useFlow'
import { useVimiumHints } from '../composables/useVimiumHints'

// Components
import FlowConnection from './FlowConnection.vue'
import RootNode from './nodeTypes/RootNode.vue'
import HintBadge from './ui/HintBadge.vue'
import { DocumentDuplicateIcon } from './icons'

const props = withDefaults(defineProps<FlowBuilderProps>(), {
  preview: false,
  canAdd: true,
  templatePreview: false,
  zoom: 1,
  disableDoubleClickZoom: false
})

// Create a simple reactive drag state instead of using Pinia
const dragState = ref<any>(null)

/**
 * Provide/Inject Pattern
 *
 * Instead of prop drilling, we provide all data dependencies at the root
 * Child components can inject only what they need, keeping them decoupled
 * This also avoids re-renders when unrelated data changes
 */
provide(
  'flow',
  computed(() => props.flow)
)
provide(
  'funnel',
  computed(() => props.flow.funnelDefinition)
)
const canAddRef = computed(() => props.canAdd)
provide('canAdd', canAddRef)
provide('templatePreview', props.templatePreview)
provide('preview', props.preview)
provide(
  'segmentGroups',
  computed(() => props.segmentGroups)
)
provide(
  'questions',
  computed(() => props.questions)
)
provide(
  'widgets',
  computed(() => props.widgets)
)
provide(
  'offers',
  computed(() => props.offers)
)
provide(
  'phrases',
  computed(() => props.phrases)
)
provide(
  'partners',
  computed(() => props.partners || [])
)
provide(
  'pages',
  computed(() => props.pages || [])
)

// Provide adapter and drag state separately
provide('adapter', props.adapter)
provide('dragState', dragState)
provide(
  'scale',
  computed(() => transform.scale)
)

// Provide icons and functions for node components
provide('icons', props.icons || {})
provide('getExternalUrl', props.getExternalUrl || ((url: string) => url))
provide(
  'customFieldLabel',
  props.customFieldLabel || (async (id: string) => id)
)
provide(
  'tagLabel',
  props.tagLabel || (async (id: string) => id)
)
provide(
  'describeLogic',
  props.describeLogic || (async () => 'Complex condition')
)
provide('getAudienceBreakdown', props.getAudienceBreakdown || (() => null))
provide('getNodeMetrics', props.getNodeMetrics || (() => null))
// Provide getRawNodeMetrics directly - it's already a function
provide('getRawNodeMetrics', props.getRawNodeMetrics || (() => null))
provide('getNodeSyncStatus', props.getNodeSyncStatus || (() => null))
provide('getNodeTest', props.getNodeTest || (() => null))
provide('getSplitTestMetrics', props.getSplitTestMetrics || (() => null))
provide(
  'integrationDataVersion',
  computed(() => props.integrationDataVersion || 0)
)
// Heat map related provides
provide(
  'showHeatMap',
  computed(() => props.showHeatMap || false)
)
provide(
  'maxVisitorCount',
  computed(() => props.maxVisitorCount || 0)
)
// Provide settings for feature toggles
provide(
  'settings',
  computed(() => props.settings || {})
)

// Provide all node props in a single object to avoid multiple inject calls
provide(
  'nodeProps',
  computed(() => ({
    icons: props.icons || {},
    offers: props.offers || {},
    getExternalUrl: props.getExternalUrl || ((url: string) => url),
    customFieldLabel: props.customFieldLabel || (async (id: string) => id),
    tagLabel: props.tagLabel || (async (id: string) => id),
    describeLogic: props.describeLogic || (async () => 'Complex condition')
  }))
)

const funnel = computed(() => props.flow.funnelDefinition)
const showPreview = ref(false)
const showHeatMap = computed(() => props.showHeatMap || false)

// Refs
const flowBuilder = ref<HTMLElement | null>(null)
const flowBuilderContainer = ref<HTMLElement | null>(null)
const nodesContainer = ref<HTMLElement | null>(null)
const teleportRoot = ref<HTMLElement | null>(null)

provide('teleportTarget', teleportRoot)
// Flow connections
const {
  connections,
  drawConnections,
  nodeElementPositions,
  heatMapNodePositions
} = useFlow(flowBuilder, nodesContainer)

// Debounced redraw function for performance
const debouncedRedraw = useDebounceFn(() => {
  drawConnections(styleVariables.value)
  updateContentDimensions()
}, 100)

/**
 * Node Preprocessing
 *
 * This watchEffect ensures data integrity:
 * 1. Assigns unique IDs to all nodes (needed for connection tracking)
 * 2. Collects all available scores from data nodes AND question options
 * 3. Cleans up highscore nodes by removing invalid score branches
 *
 * This runs whenever the flow structure changes
 */
watchEffect(() => {
  // First pass: Assign UUIDs
  funnelDefinitionEach(funnel.value, (node) => {
    // Ensure every node has a unique identifier for connection drawing
    if (!node._uuid) {
      node._uuid = uuid()
    }
  })

  // Use the centralized scoresInFlow function to get all scores
  // This correctly collects scores from both data nodes and question options
  const availableScores = scoresInFlow(funnel.value, props.questions || [])

  // Second pass: Clean up highscore nodes
  // Remove branches for scores that no longer exist
  // BUT only for score mode — variable mode branches are user-managed
  if (availableScores.length > 0) {
    funnelDefinitionEach(funnel.value, (node) => {
      if (node.type === 'highscore' && (!node.mode || node.mode === 'scores')) {
        let indexesToRemove: number[] = []
        node.split.forEach((s, i) => {
          if (!availableScores.includes(s.score) && s.score) {
            indexesToRemove.push(i)
          }
        })
        // Remove in reverse order to maintain correct indices
        indexesToRemove.reverse()
        indexesToRemove.forEach((i) => node.split.splice(i, 1))
      }
    })
  }
})

// Mouse drag handling for panning
const isPanning = ref(false)
const dragStart = reactive({ x: 0, y: 0 })
const lastTransform = reactive({ x: 0, y: 0 })

// Dropzone visibility states
const isFindMode = ref(false)
const isMouseMoving = ref(false)
let mouseIdleTimeout: ReturnType<typeof setTimeout> | null = null
const MOUSE_IDLE_DELAY = 2000 // 2 seconds before fading out

// Clone mode state: when active, all dropzones pulse gold and clicking one
// inserts a deep clone of the source node at that position
const cloneMode = ref<{ active: boolean, sourceNode: any } | null>(null)

const cancelCloneMode = () => {
  cloneMode.value = null
}

// Cancel clone mode if preview mode is activated
watch(() => props.preview, (isPreview) => {
  if (isPreview) cancelCloneMode()
})

// Computed visibility for dropzones
const showDropzones = computed(() => canAddRef.value && (isFindMode.value || isMouseMoving.value))

// Provide dropzone visibility to children
provide('showDropzones', showDropzones)
provide('isFindMode', isFindMode)
provide('cloneMode', cloneMode)

/**
 * Vimium-Style Hint System (Find Mode)
 *
 * When 'f' is pressed, displays letter badges (A, B, C...) on:
 * - Visible dropzones (for node insertion)
 * - Visible nodes (for opening/editing)
 *
 * This enables fast keyboard-driven flow navigation and editing.
 * Uses one-shot mode: automatically exits after selection.
 */
const vimiumHints = useVimiumHints({
  nodesContainer,
  flowBuilder,
  onDropzoneSelect: (data) => {
    // Notify parent app via adapter callback for dropzone selection
    props.adapter?.onDropzoneHintSelect?.(data)
    // Mode will auto-deactivate (one-shot behavior in composable)
    isFindMode.value = false
  },
  onNodeSelect: (data) => {
    // Notify parent app via adapter callback for node selection
    props.adapter?.onNodeHintSelect?.(data)
    // Mode will auto-deactivate (one-shot behavior in composable)
    isFindMode.value = false
  }
})

const handleMouseDown = (e: MouseEvent) => {
  // Only handle left click
  if (e.button !== 0) return

  // Check if we're clicking on a draggable element or its children
  const target = e.target as HTMLElement
  const draggableElement = target.closest('[draggable="true"]')

  // If clicking on a draggable element, don't start panning
  if (draggableElement) {
    return
  }

  // Check if clicking on interactive elements that shouldn't trigger pan
  // .flow-component includes mini-map and node adder which have their own drag handling
  if (target.closest('button, a, [role="button"], .flowbuilder-dropzone, .flow-component')) {
    return
  }

  isPanning.value = true
  dragStart.x = e.clientX
  dragStart.y = e.clientY
  lastTransform.x = transform.x
  lastTransform.y = transform.y

  // Change cursor
  if (flowBuilder.value) {
    flowBuilder.value.classList.add('cursor-grabbing')
    flowBuilder.value.classList.remove('cursor-grab')
  }
}

const handleMouseMove = (e: MouseEvent) => {
  // Track mouse movement for dropzone visibility (unless in find mode which is always visible)
  if (!isFindMode.value) {
    isMouseMoving.value = true

    // Clear existing timeout
    if (mouseIdleTimeout) {
      clearTimeout(mouseIdleTimeout)
    }

    // Set new timeout to hide dropzones after idle period
    mouseIdleTimeout = setTimeout(() => {
      isMouseMoving.value = false
    }, MOUSE_IDLE_DELAY)
  }

  if (!isPanning.value) return

  const deltaX = e.clientX - dragStart.x
  const deltaY = e.clientY - dragStart.y

  transform.x = lastTransform.x + deltaX
  transform.y = lastTransform.y + deltaY
  ensureWithinBoundingBox()

  // Update motion properties
  set({
    x: transform.x,
    y: transform.y,
    scale: transform.scale
  })

  // Write CSS vars directly to DOM during pan to bypass Vue reactivity.
  // This avoids triggering styleVariables computed → full re-render on every mousemove.
  if (flowBuilder.value) {
    flowBuilder.value.style.setProperty('--offsetX', `${transform.x}px`)
    flowBuilder.value.style.setProperty('--offsetY', `${transform.y}px`)
  }
}

const handleMouseUp = () => {
  const wasPanning = isPanning.value
  isPanning.value = false

  // Reset cursor
  if (flowBuilder.value) {
    flowBuilder.value.classList.remove('cursor-grabbing')
    flowBuilder.value.classList.add('cursor-grab')
  }

  // Sync Vue reactive state after pan ends so styleVariables computed stays consistent.
  // During pan we wrote CSS vars directly to DOM to avoid re-renders (Fix 2).
  // Also redraw connections since MutationObserver was gated during pan (Fix 3).
  if (wasPanning) {
    styleVariablesBase.offsetX = transform.x
    styleVariablesBase.offsetY = transform.y
    nextTick(() => {
      drawConnections(styleVariables.value)
    })
  }
}

// Handle double-click to zoom
const handleDoubleClick = (e: MouseEvent) => {
  if (!flowBuilder.value || props.disableDoubleClickZoom || !canZoomIn.value) return

  // Get click position relative to the viewport
  const viewportRect = flowBuilder.value.getBoundingClientRect()
  const clickX = e.clientX - viewportRect.left
  const clickY = e.clientY - viewportRect.top

  // Calculate the click position in the transformed space
  // We need to account for the current transform
  const scaledClickX = (clickX - transform.x) / transform.scale
  const scaledClickY = (clickY - transform.y) / transform.scale

  // Use discrete zoom system - find next zoom level
  const currentIndex = getZoomLevelIndex(transform.scale)
  const newIndex = Math.min(currentIndex + 1, zoomLevels.length - 1)
  const newScale = zoomLevels[newIndex]

  // Calculate new transform to keep the clicked point in the same position
  // After zooming, the clicked point should remain under the cursor
  const newX = clickX - scaledClickX * newScale
  const newY = clickY - scaledClickY * newScale

  // Apply the new transform with animation
  set({
    scale: newScale,
    x: newX,
    y: newY
  })

  // Update style variables
  styleVariablesBase.scale = newScale
  styleVariablesBase.offsetX = newX
  styleVariablesBase.offsetY = newY
}

// Container dimensions
const flowBuilderContainerDimensions = computed(() =>
  flowBuilderContainer.value?.getBoundingClientRect()
)

const flowBuilderDimensions = computed(() =>
  flowBuilder.value?.getBoundingClientRect()
)

// Track actual content dimensions
const contentDimensions = ref({ width: 0, height: 0 })

// Function to calculate actual content dimensions
const updateContentDimensions = () => {
  if (!nodesContainer.value) return

  // Simply use the scrollWidth and scrollHeight which gives us the actual content size
  const container = nodesContainer.value

  // Add some padding to ensure we have space around the content
  const padding = 200

  // Get the actual content dimensions including any overflow
  contentDimensions.value = {
    width: container.scrollWidth + padding * 2,
    height: container.scrollHeight + padding * 2
  }
}

// Mouse tracking
const {
  elementX: mouseX,
  elementY: mouseY,
  isOutside: mouseOutside
} = useMouseInElement(flowBuilderContainer)

// Motion setup
const { motionProperties, transform } = useMotionProperties(
  flowBuilderContainer,
  {
    cursor: 'grab',
    x: 0,
    y: 30,
    scale: props.zoom
  }
)

const { apply } = useMotionControls(motionProperties)
// Remove spring animation to prevent elastic motion on load
const { set } = useMotionControls(motionProperties)

// Style variables for CSS - use reactive for deep reactivity
const styleVariablesBase = reactive({
  scale: props.zoom,
  width: '100%',
  height: '100%',
  offsetX: 0,
  offsetY: 50,
  strokeWidth: '1px',
  connectorHeight: '50px'
})

// Computed style object that Vue will re-render when values change
const styleVariables = computed(() => ({
  '--scale': styleVariablesBase.scale,
  '--width': styleVariablesBase.width,
  '--height': styleVariablesBase.height,
  '--offsetX': `${styleVariablesBase.offsetX}px`,
  '--offsetY': `${styleVariablesBase.offsetY}px`,
  '--strokeWidth': styleVariablesBase.strokeWidth,
  '--connectorHeight': styleVariablesBase.connectorHeight
}))


// Define discrete zoom levels (max 100%)
const zoomLevels = [0.05, 0.1, 0.15, 0.25, 0.5, 0.75, 1]

// Find closest zoom level index
const getZoomLevelIndex = (currentScale: number) => {
  let closestIndex = 0
  let minDiff = Math.abs(zoomLevels[0] - currentScale)
  
  for (let i = 1; i < zoomLevels.length; i++) {
    const diff = Math.abs(zoomLevels[i] - currentScale)
    if (diff < minDiff) {
      minDiff = diff
      closestIndex = i
    }
  }
  
  return closestIndex
}

// Check if we can zoom in/out
const canZoomIn = computed(() => {
  const currentScale = motionProperties.scale || 1.0
  const currentIndex = getZoomLevelIndex(currentScale)
  return currentIndex < zoomLevels.length - 1
})

const canZoomOut = computed(() => {
  const currentScale = motionProperties.scale || 1.0
  const currentIndex = getZoomLevelIndex(currentScale)
  return currentIndex > 0
})

// Zoom functionality with discrete levels
// Uses zoom-to-center: keeps viewport center on same content point after zoom
// Note: Requires transform-origin: top left in CSS for correct math
const zoom = (amount: number, focalPoint?: { x: number; y: number }) => {
  const currentScale = motionProperties.scale || 1.0
  const currentIndex = getZoomLevelIndex(currentScale)

  let newIndex
  if (amount > 0) {
    // Zoom in: go to next level
    newIndex = Math.min(currentIndex + 1, zoomLevels.length - 1)
  } else {
    // Zoom out: go to previous level
    newIndex = Math.max(currentIndex - 1, 0)
  }

  const newScale = zoomLevels[newIndex]

  if (flowBuilder.value) {
    const viewportRect = flowBuilder.value.getBoundingClientRect()

    // Use provided focal point or default to viewport center
    const focusX = focalPoint?.x ?? viewportRect.width / 2
    const focusY = focalPoint?.y ?? viewportRect.height / 2

    // Calculate the content point at focus position in unscaled coordinates
    // Formula: content = (screen - translate) / scale
    const contentFocusX = (focusX - transform.x) / transform.scale
    const contentFocusY = (focusY - transform.y) / transform.scale

    // Calculate new transform to keep focus point stationary
    // Formula: new_translate = screen - content * new_scale
    const newX = focusX - contentFocusX * newScale
    const newY = focusY - contentFocusY * newScale

    set({
      scale: newScale,
      x: newX,
      y: newY
    })

    styleVariablesBase.scale = newScale
    styleVariablesBase.offsetX = newX
    styleVariablesBase.offsetY = newY
  } else {
    // Fallback if no ref (shouldn't happen)
    set({
      scale: newScale,
      x: transform.x,
      y: transform.y
    })
    styleVariablesBase.scale = newScale
  }
}

// Center the flow
const center = () => {
  const { width: containerWidth, height: containerHeight } =
    nodesContainer.value?.getBoundingClientRect() || { width: 0, height: 0 }
  const { width: flowWidth, height: flowHeight } =
    flowBuilder.value?.getBoundingClientRect() || { width: 0, height: 0 }

  // Calculate the scale to fit the content
  const scaleX = flowWidth / containerWidth
  const scaleY = flowHeight / containerHeight
  let scale = Math.min(scaleX, scaleY, 1)

  if (scale < 0.1) {
    scale = 0.5
  }

  // Calculate offsets to center the content
  // The flow nodes are centered within the container via flex.
  // After scaling, the center of the content is at (containerWidth * scale) / 2.
  // We need to translate so this center aligns with the viewport center.
  const offsetX = flowWidth / 2 - (containerWidth * scale) / 2

  // Update
  set({ scale, x: offsetX, y: transform.y })

  styleVariablesBase.scale = scale
  styleVariablesBase.offsetX = offsetX
}

const centerOnNode = (node: any) => {
  // Set to 100% zoom using discrete system
  const targetIndex = zoomLevels.indexOf(1)
  if (targetIndex !== -1) {
    set({
      scale: 1,
      x: transform.x,
      y: transform.y
    })
    styleVariablesBase.scale = 1
  }
  
  nextTick(() => {
    const nodeElement = findNodeElement(node)
    if (!nodeElement || !flowBuilder.value) return

    // Get the viewport (flowBuilder) bounds
    const viewportRect = flowBuilder.value.getBoundingClientRect()

    // Get the node's current position in viewport coordinates
    const nodeRect = nodeElement.getBoundingClientRect()

    // Calculate current node center in viewport coordinates
    const nodeCenterX = nodeRect.left + nodeRect.width / 2
    const nodeCenterY = nodeRect.top + nodeRect.height / 2

    // Calculate viewport center
    const viewportCenterX = viewportRect.left + viewportRect.width / 2
    const viewportCenterY = viewportRect.top + viewportRect.height / 2

    // Calculate the delta needed to center the node
    const deltaX = viewportCenterX - nodeCenterX
    const deltaY = viewportCenterY - nodeCenterY

    // Apply the delta to the current transform
    set({
      x: transform.x + deltaX,
      y: transform.y + deltaY,
      scale: transform.scale
    })
  })
}

// Helper function to find the DOM element for a node
const findNodeElement = (node: any) => {
  if (!node._uuid) return null

  // Look for elements with data-node-id attribute that are NOT children of .flow-map
  const selector = `[data-node-id="${node._uuid}"]:not(.flow-map [data-node-id="${node._uuid}"])`
  return document.querySelector(selector)
}

// Helper function to find a node in the flow by its UUID
const findNodeByUuid = (targetUuid: string): any => {
  if (!props.flow?.funnelDefinition) return null

  let foundNode = null
  funnelDefinitionEach(props.flow.funnelDefinition, (node) => {
    if (node._uuid === targetUuid) {
      foundNode = node
    }
  })
  return foundNode
}

const verticalBoundaryBuffer = 100
const horizontalBoundaryBuffer = 500
const ensureWithinBoundingBox = () => {
  if (!flowBuilderDimensions.value || !flowBuilderContainerDimensions.value)
    return

  if (transform.scale < 0.5) return

  // Don't update content dimensions here - it's too expensive to do on every scroll
  // It's already updated when nodes change via MutationObserver

  // Vertical bounds
  if (transform.y > verticalBoundaryBuffer) transform.y = verticalBoundaryBuffer

  // Calculate the minimum Y position to show all content
  // Use content height instead of container height
  const effectiveContentHeight = Math.max(
    contentDimensions.value.height,
    flowBuilderContainerDimensions.value.height
  )
  const minY =
    -(
      (effectiveContentHeight - flowBuilderDimensions.value.height) *
      transform.scale
    ) - verticalBoundaryBuffer

  if (transform.y < minY) {
    transform.y = minY
  }

  // Horizontal bounds
  if (transform.x > horizontalBoundaryBuffer)
    transform.x = horizontalBoundaryBuffer

  // Calculate the minimum X position to show all content
  const effectiveContentWidth = Math.max(
    contentDimensions.value.width,
    flowBuilderContainerDimensions.value.width
  )
  const minX =
    -(
      (effectiveContentWidth - flowBuilderDimensions.value.width) *
      transform.scale
    ) - horizontalBoundaryBuffer

  if (transform.x < minX) {
    transform.x = minX
  }
}

// Pinch to zoom
const pinchHandler = ({ offset: [d, a], pinching }) => {
  // Implement pinch zoom if needed
}

usePinch(pinchHandler, {
  domTarget: flowBuilder,
  eventOptions: {
    passive: false
  }
})

// Wheel scrolling
const lethargy = new Lethargy(30, 50, 0.05)

useWheel(
  ({ velocities: [x, y], event: scrollEvent }) => {
    scrollEvent.preventDefault()
    scrollEvent.stopPropagation()
    transform.y = transform.y - y * 5
    transform.x = transform.x - x * 5

    ensureWithinBoundingBox()
  },
  {
    domTarget: flowBuilder,
    eventOptions: { passive: false }
  }
)

// Keyboard shortcuts using VueUse onKeyStroke
// These prevent default browser behavior (native zoom)

// Zoom in: Cmd/Ctrl + = (or +)
onKeyStroke(['=', '+'], (e) => {
  if (props.preview) return
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault()
    if (canZoomIn.value) zoom(1)
  }
})

// Zoom out: Cmd/Ctrl + -
onKeyStroke('-', (e) => {
  if (props.preview) return
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault()
    if (canZoomOut.value) zoom(-1)
  }
})

// Zoom to fit: Cmd/Ctrl + 0
onKeyStroke('0', (e) => {
  if (props.preview) return
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault()
    center()
  }
})

/**
 * Vimium Hint Key Capture (Find Mode)
 *
 * CRITICAL: This handler MUST come before other letter handlers (s, g, h/j/k/l)
 * When find mode is active, ALL letter keys are captured here
 * to build the hint label buffer. This prevents conflicts with search (s),
 * navigation (hjkl), and other shortcuts.
 *
 * Uses useEventListener instead of onKeyStroke because we need to capture
 * ALL letter keys dynamically, and onKeyStroke requires specifying keys upfront.
 */
useEventListener('keydown', (e: KeyboardEvent) => {
  // Don't capture keyboard events in preview mode
  if (props.preview) return

  // Handle Escape for clone mode (higher priority than hints)
  if (e.key === 'Escape' && cloneMode.value?.active) {
    e.preventDefault()
    cancelCloneMode()
    return
  }

  // Only capture when hints are actively capturing
  if (!vimiumHints.isCapturing.value) return

  // Don't capture if in an editable element
  if (isInEditableElement(e)) return
  // Don't capture special keys or modifiers
  if (e.metaKey || e.ctrlKey || e.altKey) return

  const key = e.key.toUpperCase()

  // Handle Escape to exit find mode
  if (e.key === 'Escape') {
    e.preventDefault()
    vimiumHints.deactivate()
    isFindMode.value = false
    return
  }

  // Handle letter keys (A-Z) EXCEPT navigation keys (H,J,K,L), 'F' (toggle find mode), and 'S' (search)
  const reservedKeys = ['H', 'J', 'K', 'L', 'F', 'S']
  if (/^[A-Z]$/.test(key) && !reservedKeys.includes(key)) {
    e.preventDefault()
    e.stopImmediatePropagation() // Block other handlers like 's' for search
    vimiumHints.handleKeyStroke(key)
    return
  }

  // Handle Backspace to clear last character
  if (e.key === 'Backspace') {
    e.preventDefault()
    vimiumHints.clearLastChar()
    return
  }
}, { capture: true })

// Search: 's' opens search dialog (works in find mode too - 's' is reserved)
// Only triggers when not focused on an input element
onKeyStroke('s', (e) => {
  if (props.preview) return

  // Skip if user is typing in an input, textarea, or contenteditable
  const target = e.target as HTMLElement
  const tagName = target.tagName.toLowerCase()
  const isEditable =
    tagName === 'input' ||
    tagName === 'textarea' ||
    target.isContentEditable ||
    target.closest('[contenteditable="true"]')

  if (isEditable) return

  // Don't trigger if any modifier key is pressed
  if (e.metaKey || e.ctrlKey || e.altKey) return

  e.preventDefault()
  props.adapter?.onOpenSearch?.()
})

// '/' also activates find mode (Vim-style: / for find on screen)
onKeyStroke('/', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
  e.preventDefault()

  // Activate find mode (one-shot)
  isFindMode.value = true
  vimiumHints.activate()

  // Clear any pending idle timeout
  if (mouseIdleTimeout) {
    clearTimeout(mouseIdleTimeout)
    mouseIdleTimeout = null
  }
})

// Helper function to check if user is typing in an editable element
const isInEditableElement = (e: KeyboardEvent): boolean => {
  const target = e.target as HTMLElement
  const tagName = target.tagName.toLowerCase()
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    target.isContentEditable ||
    !!target.closest('[contenteditable="true"]')
  )
}

// Panning constants
const PAN_STEP = 80 // pixels per arrow key press
const HALF_PAGE_MULTIPLIER = 0.5 // percentage of viewport height for d/u

// Helper function to apply pan delta
const applyPanDelta = (deltaX: number, deltaY: number) => {
  transform.x += deltaX
  transform.y += deltaY
  ensureWithinBoundingBox()

  set({
    x: transform.x,
    y: transform.y,
    scale: transform.scale
  })

  styleVariablesBase.offsetX = transform.x
  styleVariablesBase.offsetY = transform.y
}

// Arrow keys for panning (Up/Down/Left/Right)
onKeyStroke('ArrowUp', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  e.preventDefault()
  applyPanDelta(0, PAN_STEP)
})

onKeyStroke('ArrowDown', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  e.preventDefault()
  applyPanDelta(0, -PAN_STEP)
})

onKeyStroke('ArrowLeft', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  e.preventDefault()
  applyPanDelta(PAN_STEP, 0)
})

onKeyStroke('ArrowRight', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  e.preventDefault()
  applyPanDelta(-PAN_STEP, 0)
})

// Vim-style hjkl for panning (works in find mode too - these keys are reserved)
onKeyStroke('h', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  e.preventDefault()
  applyPanDelta(PAN_STEP, 0) // Left
})

onKeyStroke('j', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  e.preventDefault()
  applyPanDelta(0, -PAN_STEP) // Down
})

onKeyStroke('k', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  e.preventDefault()
  applyPanDelta(0, PAN_STEP) // Up
})

onKeyStroke('l', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  e.preventDefault()
  applyPanDelta(-PAN_STEP, 0) // Right
})

// Ctrl/Cmd+D for half-page down (vim Ctrl+D style)
onKeyStroke('d', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  // Require Ctrl or Cmd modifier
  if (!(e.metaKey || e.ctrlKey)) return
  if (e.altKey || e.shiftKey) return
  e.preventDefault()

  const viewportHeight = flowBuilderDimensions.value?.height || 600
  const halfPage = viewportHeight * HALF_PAGE_MULTIPLIER
  applyPanDelta(0, -halfPage)
})

// Ctrl/Cmd+U for half-page up (vim Ctrl+U style)
onKeyStroke('u', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  // Require Ctrl or Cmd modifier for half-page navigation
  if (!(e.metaKey || e.ctrlKey)) return
  if (e.altKey || e.shiftKey) return
  e.preventDefault()

  const viewportHeight = flowBuilderDimensions.value?.height || 600
  const halfPage = viewportHeight * HALF_PAGE_MULTIPLIER
  applyPanDelta(0, halfPage)
})

// 'u' without modifier for undo (vim style)
onKeyStroke('u', (e) => {
  if (props.preview) return
  if (vimiumHints.isCapturing.value) return // Skip when hints active
  if (isInEditableElement(e)) return
  // Only trigger without any modifier keys
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
  e.preventDefault()
  props.adapter?.onUndo?.()
})

// Ctrl/Cmd+Z for undo (standard)
onKeyStroke('z', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (!(e.metaKey || e.ctrlKey)) return
  if (e.altKey) return
  // Shift+Z is redo on Mac
  if (e.shiftKey) {
    e.preventDefault()
    props.adapter?.onRedo?.()
  } else {
    e.preventDefault()
    props.adapter?.onUndo?.()
  }
})

// Ctrl+Y for redo (Windows/Linux standard)
onKeyStroke('y', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (!(e.metaKey || e.ctrlKey)) return
  if (e.altKey || e.shiftKey) return
  e.preventDefault()
  props.adapter?.onRedo?.()
})

// Track last 'g' press time for 'gg' command
let lastGPressTime = 0
const GG_TIMEOUT = 300 // milliseconds to detect double 'g'

// 'g' handler for 'gg' (jump to top)
onKeyStroke('g', (e) => {
  if (props.preview) return
  if (vimiumHints.isCapturing.value) return // Skip when hints active
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return

  const now = Date.now()
  if (now - lastGPressTime < GG_TIMEOUT) {
    // Double 'g' detected - jump to top
    e.preventDefault()
    lastGPressTime = 0 // Reset

    // Jump to top: reset Y to initial position (showing the root node)
    transform.y = 30 // Default initial Y position
    ensureWithinBoundingBox()

    set({
      x: transform.x,
      y: transform.y,
      scale: transform.scale
    })

    styleVariablesBase.offsetY = transform.y
  } else {
    // First 'g' press - record time
    lastGPressTime = now
  }
})

// 'G' (capital) for jump to bottom
onKeyStroke('G', (e) => {
  if (props.preview) return
  if (vimiumHints.isCapturing.value) return // Skip when hints active
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey) return
  // Note: shiftKey will be true for capital G, that's expected
  e.preventDefault()

  if (!flowBuilderDimensions.value || !contentDimensions.value) return

  // Calculate Y position to show the bottom of the content
  const viewportHeight = flowBuilderDimensions.value.height
  const contentHeight = contentDimensions.value.height * transform.scale

  // Position so bottom of content is visible at bottom of viewport
  const bottomY = -(contentHeight - viewportHeight) - verticalBoundaryBuffer

  transform.y = bottomY
  ensureWithinBoundingBox()

  set({
    x: transform.x,
    y: transform.y,
    scale: transform.scale
  })

  styleVariablesBase.offsetY = transform.y
})

// 'f' activates find mode (shows hints on dropzones and nodes)
// One-shot mode: automatically exits after selection
onKeyStroke('f', (e) => {
  if (props.preview) return
  if (isInEditableElement(e)) return
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
  e.preventDefault()

  // Activate find mode (one-shot)
  isFindMode.value = true
  vimiumHints.activate()

  // Clear any pending idle timeout
  if (mouseIdleTimeout) {
    clearTimeout(mouseIdleTimeout)
    mouseIdleTimeout = null
  }
})


// Debounced hint refresh for performance during pan/zoom
const debouncedRefreshHints = useDebounceFn(() => {
  if (vimiumHints.isActive.value) {
    vimiumHints.refreshHints()
  }
}, 100)

// Watch for transform changes to refresh hints (visible dropzones change on pan/zoom)
watch(
  () => [transform.x, transform.y, transform.scale],
  () => {
    debouncedRefreshHints()
  }
)

// Watch for integration data changes and redraw connections
watch(
  () => props.integrationDataVersion,
  () => {
    // When integration data changes, nodes might resize due to new text
    // Wait for DOM updates then redraw connections
    nextTick(() => {
      drawConnections(styleVariables.value)
      // Also update content dimensions in case nodes grew
      updateContentDimensions()
    })
  }
)

// Setup
onMounted(() => {
  // Watch for mutations
  useMutationObserver(
    nodesContainer,
    () => {
      // Skip redraw during pan — connections will be redrawn on mouseup
      if (isPanning.value) return

      nextTick(() => {
        drawConnections(styleVariables.value)
        // Update content dimensions when nodes change
        updateContentDimensions()
        // Ensure we're still within bounds with new content
        ensureWithinBoundingBox()
        // Refresh hints if in find mode (visible elements may have changed)
        debouncedRefreshHints()
      })
    },
    {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-branch-terminates']
    }
  )

  // Initial draw
  drawConnections(styleVariables.value)

  // Initial content dimensions calculation
  nextTick(() => {
    updateContentDimensions()
  })

  // Watch for viewport size changes
  useResizeObserver(flowBuilder, () => {
    ensureWithinBoundingBox()
  })

  if (props.preview && !props.templatePreview) {
    nextTick(() => {
      center()
    })
  } else {
    nextTick(() => {
      const offsetX =
        -1 *
        (flowBuilderContainerDimensions.value.width / 2 -
          flowBuilderDimensions.value.width / 2)
      // Use set instead of apply to avoid animation on initial load
      set({
        x: offsetX,
        y: transform.y
      })
    })
  }
})

// Watch for resize
useResizeObserver(flowBuilder, () => {
  drawConnections(styleVariables.value)
})

// Watch for nodes container resize (catches node dimension changes)
useResizeObserver(nodesContainer, () => {
  debouncedRedraw()
})

// Expose methods
defineExpose({
  zoom,
  center,
  centerOnNode,
  findNodeByUuid,
  set: (newTransform: any) => {
    transform.scale = newTransform.scale
    transform.x = newTransform.x
    transform.y = newTransform.y
  },
  scale: computed(() => transform.scale),
  transform: computed(() => transform),
  contentDimensions: computed(() => contentDimensions.value),
  $el: flowBuilder,
  redraw: () => drawConnections(styleVariables.value),
  forceRedraw: () => {
    nextTick(() => {
      drawConnections(styleVariables.value)
      updateContentDimensions()
    })
  },
  cancelCloneMode
})
</script>
